import { lazy, Suspense, useEffect, useState } from 'react';
import {
    Link,
    Navigate,
    Route,
    Routes,
    useLocation,
    useSearchParams,
} from 'react-router-dom';
import { useSelector } from 'react-redux';
import { Layout, Grid, message, theme } from 'antd';
import {
    pageview,
    gtagInit,
    Loader,
    AppHeader,
    AppLayout,
    VideoModal,
    lazyShared,
} from '@inscore/shared';

import { ReactComponent as LogoDark } from 'assets/logo-dark.svg';
import { ReactComponent as LogoLight } from 'assets/logo-light.svg';
import { ReactComponent as RuStore } from 'assets/icons/ru-store.svg';
import { ReactComponent as RuStoreMobile } from 'assets/icons/ru-store-mobile.svg';

import RightSidebar from 'components/rightSidebar/RightSidebar';
import AppFooter from 'components/appFooter/AppFooter';
import TopBanner from 'components/topBanner/TopBanner';
import AdsApi from 'api/AdsApi';

import { burgerMenuItems } from './appService';
import useMenuItems from './useMenuItems';

import './index.css';

const ContactUs = lazyShared('ContactUs');
const PrivacyPolicy = lazyShared('PrivacyPolicy');
const Search = lazyShared('Search');
const TermsOfUse = lazyShared('TermsOfUse');
const Favorites = lazyShared('Favorites');
const Matches = lazyShared('Matches');
const News = lazyShared('News');
const Goals = lazyShared('Goals');
const LeagueDetails = lazyShared('LeagueDetails');
const FixtureDetails = lazyShared('FixtureDetails');
const TeamDetails = lazyShared('TeamDetails');
const PlayerDetails = lazyShared('PlayerDetails');
const Stats = lazyShared('Stats');
const StatsTeamFixtures = lazyShared('StatsTeamFixtures');
const Bookmakers = lazy(() => import('pages/bookmakers/Bookmakers'));

const { useBreakpoint } = Grid;

export const GA_TRACKING_ID = 'G-HCLCE61VV9';

const App = () => {
    const { menuItems } = useMenuItems();
    const { token } = theme.useToken();
    const storeTheme = useSelector((state) => state.theme?.mode);
    const screens = useBreakpoint();
    const isMobile = !screens.lg;
    const { pathname, search } = useLocation();
    const [searchParams] = useSearchParams();
    const [drawerOpened, setDrawerOpened] = useState(false);
    const [messageApi, contextHolder] = message.useMessage();
    const [adsAvailable, setAdsAvailable] = useState(false);

    const pathnameFirstSegment = pathname.split('/')[1];
    const hideSideBars = ['bookmakers'].includes(pathnameFirstSegment);
    const hideFooter =
        pathname.includes('/search') ||
        pathname.includes('/privacy-policy') ||
        pathname.includes('/terms-conditions');
    const hideHeader =
        searchParams.get('mode') === 'fromapp' ||
        (isMobile && pathname.includes('/fixture/'));
    const headerHeight = hideHeader
        ? 0
        : isMobile
          ? 60 + (adsAvailable ? 68 : 0)
          : 96;
    const Logo = storeTheme === 'dark' ? LogoDark : LogoLight;

    useEffect(() => {
        gtagInit(GA_TRACKING_ID);
        window.messageApi = messageApi;
    }, []);

    useEffect(() => {
        const getAdsApi = AdsApi.getAvailability();
        getAdsApi
            .call()
            .then((response) => {
                setAdsAvailable(response?.data?.adsAvailable ?? true);
            })
            .catch((error) => {
                console.error('Failed to fetch ads availability:', error);
            });

        return () => {
            getAdsApi.cancel();
        };
    }, []);

    useEffect(() => {
        if (isMobile) {
            setDrawerOpened(false);
        }
    }, [pathname]);

    useEffect(() => {
        pageview(pathname + search);
    }, [pathname, search]);

    // const containerClassName =
    //     pathname.includes('/contact-us') ||
    //     pathname.includes('/videos') ||
    //     pathname.includes('/bookmakers') ||
    //     pathname.includes('/fixture/') ||
    //     pathname.includes('/news')
    //         ? ''
    //         : 'container-lg';

    const containerClassName = ''; // tbd: test

    return (
        <Layout
            className="naija-score-app"
            style={{
                padding: isMobile
                    ? hideHeader
                        ? 0
                        : `${headerHeight + 16}px 0 ${hideHeader ? '0' : '56px'} 0`
                    : `${headerHeight + 24}px 0 0 0`,
            }}
        >
            <div
                style={{
                    padding: isMobile ? '0 16px' : '0 24px',
                }}
            >
                {contextHolder}

                {!hideHeader && (
                    <AppHeader
                        showSelectLanguage={false}
                        drawerOpened={drawerOpened}
                        setDrawerOpened={setDrawerOpened}
                        menuItems={menuItems}
                        burgerMenuItems={burgerMenuItems}
                        topBanner={
                            adsAvailable && isMobile ? <TopBanner /> : null
                        }
                        additionalNavigationItem={
                            <Link
                                style={{
                                    display: 'inline-flex',
                                    position: 'relative',
                                    zIndex: 1,
                                }}
                                onClick={() =>
                                    window.open(
                                        'https://www.rustore.ru/catalog/app/com.profootball.profootball?_rsc=Bl87od1bNOqMZDgt',
                                        '_blank',
                                        'noopener noreferrer'
                                    )
                                }
                            >
                                <RuStore />
                            </Link>
                        }
                    >
                        <Logo style={{ width: screens.md ? 210 : 144 }} />
                    </AppHeader>
                )}
                <Layout
                    className="container"
                    style={{
                        minHeight: `calc(100vh - ${isMobile ? '210px' : '120px'} - ${hideFooter ? '0px' : isMobile ? '235px' : '199px'})`,
                    }}
                >
                    <Suspense fallback={<Loader />}>
                        <Routes>
                            <Route
                                path="/"
                                element={
                                    <AppLayout
                                        showSelectLanguage={false}
                                        headerHeight={headerHeight}
                                        hideSideBars={hideSideBars}
                                        drawerOpened={drawerOpened}
                                        setDrawerOpened={setDrawerOpened}
                                        burgerMenuItems={burgerMenuItems}
                                        containerClassName={containerClassName}
                                        rightSidebar={
                                            adsAvailable ? (
                                                <RightSidebar
                                                    token={token}
                                                    stickyOffset={
                                                        headerHeight + 24
                                                    }
                                                />
                                            ) : null
                                        }
                                    />
                                }
                            >
                                <Route path="matches" element={<Matches />} />
                                <Route
                                    path="favorites"
                                    element={<Favorites />}
                                />
                                <Route
                                    path="league/:id"
                                    element={<LeagueDetails />}
                                />
                                <Route
                                    path="fixture/:id"
                                    element={<FixtureDetails />}
                                />
                                <Route
                                    path="team/:id"
                                    element={<TeamDetails />}
                                />
                                <Route
                                    path="player/:id"
                                    element={<PlayerDetails />}
                                />
                                <Route
                                    path="news"
                                    element={
                                        <News containerClassName="container-lg" />
                                    }
                                />
                                <Route
                                    path="videos"
                                    element={
                                        <Goals containerClassName="container-xs margin-center" />
                                    }
                                />
                                <Route
                                    path="contact-us"
                                    element={<ContactUs />}
                                />
                                <Route
                                    path="stats"
                                    element={<Stats token={token} />}
                                />
                                <Route
                                    path="stats/team/:teamId"
                                    element={<StatsTeamFixtures />}
                                />
                                <Route
                                    path="bookmakers"
                                    element={<Bookmakers />}
                                />
                                <Route
                                    path="/"
                                    element={<Navigate to="/matches" replace />}
                                />
                            </Route>

                            <Route path="/search" element={<Search />} />
                            <Route
                                path="/privacy-policy"
                                element={<PrivacyPolicy />}
                            />
                            <Route
                                path="/terms-conditions"
                                element={<TermsOfUse />}
                            />

                            <Route
                                path="*"
                                element={<Navigate to="/matches" replace />}
                            />
                        </Routes>
                    </Suspense>
                </Layout>

                <Suspense fallback={<Loader />}>
                    <VideoModal />
                </Suspense>
            </div>
            {!hideFooter && (
                <Suspense fallback={<Loader />}>
                    <AppFooter
                        additionalStoreLink={
                            !screens.lg ? (
                                <Link
                                    to="https://www.rustore.ru/catalog/app/com.profootball.profootball?_rsc=Bl87od1bNOqMZDgt"
                                    target="_blank"
                                    rel="noopener noreferrer"
                                >
                                    <RuStoreMobile />
                                </Link>
                            ) : null
                        }
                    >
                        <Logo
                            style={{
                                width: screens.md ? 177 : 144,
                                height: 'auto',
                            }}
                        />
                    </AppFooter>
                </Suspense>
            )}
        </Layout>
    );
};

export default App;
